import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Box,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
  Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  MicrosoftLoginButton,
  AppleLoginButton,
  GoogleLoginButton,
  AmazonLoginButton,
  FacebookLoginButton,
} from 'react-social-login-buttons';
import { Auth } from '@aws-amplify/auth';

import validationUtils from '../../services/validationUtils';
import TextInput from '../form/TextInput';

const { __environment__ } = window as any;

const PREFIX = 'LoginForm';

const classes = {
  container: `${PREFIX}-container`,
  box: `${PREFIX}-box`,
  form: `${PREFIX}-form`,
  title: `${PREFIX}-title`,
  password: `${PREFIX}-password`,
  error: `${PREFIX}-error`,
  submit: `${PREFIX}-submit`,
  signup: `${PREFIX}-signup`,
  link: `${PREFIX}-link`,
  disclaimer: `${PREFIX}-disclaimer`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    padding: 32,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 600,
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      width: 300,
      marginBottom: 50,
    },
  },

  [`& .${classes.box}`]: {
    width: '48%',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },

  [`& .${classes.box}:nth-of-type(2)`]: {
    [theme.breakpoints.down('lg')]: {
      marginTop: 50,
    },
  },

  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.password}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.error}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.submit}`]: {
    marginTop: theme.spacing(4),
  },

  [`& .${classes.signup}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.link}`]: {
    display: 'block',
    marginTop: theme.spacing(4),
  },

  [`& .${classes.disclaimer}`]: {
    fontSize: 14,
    paddingLeft: 15,
    paddingRight: 15,
    marginTop: 15,
  },
}));

interface LoginFormProps {
  returnPage?: string;
  error?: string;
  submit: (info: any) => void;
}

export default function LoginForm({
  returnPage,
  error,
  submit,
}: LoginFormProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const requiredFieldMessage = t('common.requiredField');
  const { control, formState, handleSubmit, getValues } = useForm({
    defaultValues: {
      Email: '',
      Password: '',
    } as any,
  });
  const [passwordVisible, setPasswordVisible] = React.useState(false);

  return (
    <Root className={classes.container}>
      <Box className={classes.box}>
        <Typography className={classes.title}>
          {t('auth.loginTitle')}
        </Typography>
        <form onSubmit={handleSubmit(submit)} className={classes.form}>
          <TextInput
            label={t('auth.username')}
            name={'Email'}
            required={true}
            type="email"
            control={control}
            errors={formState.errors}
            rules={{
              required: requiredFieldMessage,
              pattern: validationUtils.emailValidation(t),
            }}
          />
          <FormControl variant="outlined" className={classes.password}>
            <InputLabel htmlFor="password" required={true}>
              {t('auth.password')}
            </InputLabel>
            <Controller
              name={'Password'}
              control={control}
              defaultValue={''}
              rules={{
                required: requiredFieldMessage,
              }}
              render={({ field }) => (
                <OutlinedInput
                  {...field}
                  id="password"
                  type={passwordVisible ? 'text' : 'password'}
                  required={true}
                  error={
                    formState.errors ? !!formState.errors['Password'] : false
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        onMouseDown={(evt) => evt.preventDefault()}
                        edge="end">
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t('auth.password')}
                />
              )}
            />
          </FormControl>
          <Typography color="error" className={classes.error}>
            {error}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            className={classes.submit}
            type="submit">
            {t('auth.login')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.signup}
            onClick={(evt) => {
              evt.preventDefault();
              navigate('/signup');
            }}>
            {t('auth.signup')}
          </Button>
        </form>
        <Link
          href="#"
          onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            evt.preventDefault();
            navigate('/forgotPassword');
          }}
          color="primary"
          underline="hover"
          align="center"
          className={classes.link}>
          {t('auth.forgotPassword')}
        </Link>
        <Link
          href="#"
          onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            evt.preventDefault();
            navigate(`/internalLogin?returnPage=${returnPage}`);
          }}
          className={classes.link}
          color="primary"
          underline="hover"
          align="center">
          {t('auth.internalLogin')}
        </Link>
      </Box>

      <Box className={classes.box}>
        <Typography className={classes.title}>
          {t('auth.loginOther')}
        </Typography>
        <MicrosoftLoginButton
          onClick={() =>
            Auth.federatedSignIn({
              customProvider: __environment__.REACT_APP_AWS_MICROSOFT_PROVIDER,
            })
          }
          text={t('auth.continueWithMicrosoft')}
          style={{ fontSize: 14, height: 40 }}
        />
        <AppleLoginButton
          onClick={() =>
            Auth.federatedSignIn({
              customProvider: __environment__.REACT_APP_AWS_APPLE_PROVIDER,
            })
          }
          text={t('auth.continueWithApple')}
          style={{ fontSize: 14, height: 40, marginTop: 20 }}
        />
        <AmazonLoginButton
          onClick={() =>
            Auth.federatedSignIn({
              customProvider: __environment__.REACT_APP_AWS_AMAZON_PROVIDER,
            })
          }
          text={t('auth.continueWithAmazon')}
          style={{ fontSize: 14, height: 40, marginTop: 20, color: '#000000' }}
          iconColor="#000000"
        />
        <GoogleLoginButton
          onClick={() =>
            Auth.federatedSignIn({
              customProvider: __environment__.REACT_APP_AWS_GOOGLE_PROVIDER,
            })
          }
          text={t('auth.continueWithGoogle')}
          style={{ fontSize: 14, height: 40, marginTop: 20 }}
        />
        <FacebookLoginButton
          onClick={() =>
            Auth.federatedSignIn({
              customProvider: __environment__.REACT_APP_AWS_FACEBOOK_PROVIDER,
            })
          }
          text={t('auth.continueWithFacebook')}
          style={{ fontSize: 14, height: 40, marginTop: 20 }}
        />
        <Typography className={classes.disclaimer}>
          {t('auth.disclaimer')}
        </Typography>
      </Box>
    </Root>
  );
}
